import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { isBhooklyNativeServerApp, nativeSetBranding } from '@/lib/bhooklyNative'
import { useTheme } from '@/contexts/ThemeContext'
import { canvasFor } from '@/lib/themeCanvas'
import { desktop } from '@/lib/printTransport'

function setLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
}

function mimeFromUrl(u: string): string {
  const m = /^data:(image\/[a-z0-9.+-]+)/i.exec(u)
  return m ? m[1] : 'image/png'
}

/**
 * Point every <meta name="theme-color"> at the resolved canvas.
 *
 * This is what the OS paints behind an installed PWA before the first frame,
 * and what tints the browser chrome on Android. It was a static '#0f172a' in
 * index.html and never resynced, so a light-theme POS flashed slate-navy on
 * every launch. Mirrors the storefront's syncMetaThemeColor.
 */
function syncMetaThemeColor(hex: string) {
  const tags = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
  if (tags.length === 0) {
    const meta = document.createElement('meta')
    meta.name = 'theme-color'
    meta.content = hex
    document.head.appendChild(meta)
    return
  }
  tags.forEach((t) => {
    // Drop any media gate — we resolve the theme ourselves and deliberately
    // do not follow prefers-color-scheme.
    t.removeAttribute('media')
    t.content = hex
  })
}

// Re-brands the installable POS app with the tenant's logo + name once settings
// load (post-login), so the home-screen icon on a tablet is THEIR logo. Falls
// back to the bundled "B" icon / "Bhookly POS" manifest when no logo is set.
export function PwaBranding() {
  const { data } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const d = data?.data as Record<string, unknown> | undefined
  const logo = typeof d?.['receipt_logo_url'] === 'string' ? (d['receipt_logo_url'] as string) : ''
  const name = typeof d?.['receipt_business_name'] === 'string' ? (d['receipt_business_name'] as string) : ''
  const { theme } = useTheme()
  const canvas = canvasFor(theme)

  // Runs on every theme flip, independent of whether the tenant has a logo —
  // the manifest rebuild below is gated on `logo`, but chrome tint must track
  // the theme for every tenant.
  useEffect(() => {
    syncMetaThemeColor(canvas)
    // And tell the Electron shell, so the window background behind the
    // renderer matches instead of staying on whatever it opened with.
    void desktop()?.setThemeCanvas?.(canvas)
  }, [canvas])

  useEffect(() => {
    // Bhookly Server APK: push logo into the Android top bar + recents icon.
    if (isBhooklyNativeServerApp()) {
      nativeSetBranding(logo, name)
    }
    if (!logo) return
    setLink('apple-touch-icon', logo)
    setLink('icon', logo)
    const manifest = {
      name: name ? `${name} POS` : 'Bhookly POS',
      short_name: name || 'POS',
      description: 'Point of Sale — counter, kitchen (KDS) and admin.',
      display: 'standalone',
      scope: '/',
      start_url: '/',
      theme_color: canvas,
      background_color: canvas,
      icons: [{ src: logo, sizes: 'any', type: mimeFromUrl(logo), purpose: 'any maskable' }],
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }))
    setLink('manifest', url)
    return () => URL.revokeObjectURL(url)
  }, [logo, name, canvas])

  return null
}
